import DSBox from "views/system-design/DSBox";
import DSTextField from "views/system-design/DSTextField";
import DSTypography from "views/system-design/DSTypography";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Ticket } from "../../types";
import { useTickets } from "../../context/TicketsContext";
import useAutoSave from "../../hooks/useAutoSave";

interface TicketInfoSectionProps {
  ticket: Ticket;
}

function AutoSaveField({
  ticketId,
  label,
  value,
  onSave,
  multiline,
  minRows,
  maxRows,
  placeholder,
  variant,
}: {
  ticketId: string;
  label?: string;
  value: string;
  onSave: (value: string) => void;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  placeholder: string;
  variant?: "title" | "block";
}) {
  const { draft, setDraft, status, flush } = useAutoSave(
    ticketId,
    value,
    onSave,
  );

  const statusLabel =
    status === "saving" ? "Saving..." : status === "saved" ? "Saved" : null;

  if (variant === "title") {
    return (
      <DSBox sx={{ mb: 2 }}>
        <DSTextField
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={flush}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              flush();
            }
          }}
          fullWidth
          size="small"
          placeholder={placeholder}
          InputProps={{
            sx: {
              fontWeight: 600,
              fontSize: "1.5rem",
            },
          }}
        />
        {statusLabel && (
          <DSTypography
            variant="caption"
            sx={{ color: "text.disabled", fontSize: 11, mt: 0.5 }}
          >
            {statusLabel}
          </DSTypography>
        )}
      </DSBox>
    );
  }

  return (
    <DSBox>
      {label && (
        <DSTypography
          variant="subtitle2"
          sx={{ color: "text.secondary", mb: 1, mt: 3 }}
        >
          {label}
        </DSTypography>
      )}
      <DSTextField
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={flush}
        fullWidth
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        placeholder={placeholder}
      />
      {statusLabel && (
        <DSTypography
          variant="caption"
          sx={{ color: "text.disabled", fontSize: 11, mt: 0.5 }}
        >
          {statusLabel}
        </DSTypography>
      )}
    </DSBox>
  );
}

export default function TicketInfoSection({ ticket }: TicketInfoSectionProps) {
  const { updateTicket } = useTickets();

  return (
    <DSBox sx={{ mb: 3 }}>
      <AutoSaveField
        ticketId={ticket.id}
        value={ticket.title}
        onSave={(title) => updateTicket(ticket.id, { title })}
        placeholder="Ticket title"
        variant="title"
      />

      <DSTypography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
        Description
      </DSTypography>
      <AutoSaveField
        ticketId={ticket.id}
        value={ticket.description}
        onSave={(description) => updateTicket(ticket.id, { description })}
        multiline
        minRows={3}
        maxRows={12}
        placeholder="Add a description..."
      />
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.5,
          mt: 0.5,
        }}
      >
        <AttachFileIcon sx={{ fontSize: 12, color: "text.disabled" }} />
        <DSTypography
          variant="caption"
          sx={{ color: "text.disabled", fontSize: 11 }}
        >
          Paste files with Ctrl+V to attach
        </DSTypography>
      </DSBox>

      <AutoSaveField
        ticketId={ticket.id}
        label="Agent Input"
        value={ticket.agentInput}
        onSave={(agentInput) => updateTicket(ticket.id, { agentInput })}
        multiline
        minRows={2}
        maxRows={12}
        placeholder="Add agent input..."
      />
    </DSBox>
  );
}
